/* eslint-disable no-use-before-define */
import { lookupObjectFromArray } from './util';

const getSampleDataAsGroovyArg = sampleDataObj => {
  let output = '[';
  Object.keys(sampleDataObj)
    .reverse()
    .forEach((key, i) => {
      const sampleDataValue = sampleDataObj[key];

      if (typeof sampleDataValue === 'object') {
        output += `${key}:'${JSON.stringify(sampleDataValue)
          .replace(/"/g, '')
          .replace(/:/g, '=')}'`;
      } else {
        output += `${key}: '${sampleDataValue}'`;
      }

      if (i < Object.keys(sampleDataObj).length - 1) output += ',';
    });
  output += ']';
  return output;
};

/**
 *
 * @param {string} path
 * @param {string} field
 * @param {boolean} isField
 * @returns Match for given field or index if present in path. Otherwise returns null.
 */

export const extractFromPath = (path = null, field, isField) => {
  if (path === null) return null;

  const FIELD_REGEX = /^(?<field>\w+)(\[(?<index>.+)])?$/;
  const INDEX_REGEX = /^(?<bucketName>\w+)(@(?<breakOutBy>\w+)(:(?<breakOutArg>\w+))?)?$/;
  const match = isField ? path.match(FIELD_REGEX) : path.match(INDEX_REGEX);
  const isMatched = match !== null;
  const output = isMatched && match.groups[field] ? match.groups[field] : null;
  return output;
};

/**
 *
 * @param {string} mappingValue
 * @param {object} metadata
 * @returns List of objects with a path & the corrisponding groovy wrapper function
 */

export const getPathValue = (mappingValue, { sampleData, path }, index = null) => {
  const outputValues = [];
  let sampleDataValue;

  if (path.includes('@segment:')) {
    const sampleDataKey = path.match(/@segment:\w+/g)[0].replace('@segment:', '');
    const iterator = path.match(/(?<=\[)(.*?)(?=\@)/)[0];
    const field = extractFromPath(path.split('.')[0], 'field', true);
    const sampleDataValueList = sampleData[sampleDataKey];
    const pathProperties = path.slice(Math.max(0, path.indexOf(']') + 1));

    if (sampleDataValueList !== null) {
      sampleDataValue = sampleDataValueList[index];
      const valToReplace = mappingValue.split('.').filter(str => !str.endsWith('()'));
      const sampleDataLookupKey = valToReplace[1];
      let groovyArgGenerated = mappingValue;

      if (sampleDataLookupKey) {
        groovyArgGenerated = groovyArgGenerated.replace(
          valToReplace.join('.'),
          `"${sampleDataValue[sampleDataLookupKey]}"`,
        );
      }

      if (typeof index === 'number') {
        groovyArgGenerated = groovyArgGenerated.replace(`${iterator}_index`, index);
      }

      const dynamicPath = `${field}[${index}]${pathProperties}`;
      const groovyStr = `static def groovyWrapper() {\n return ${groovyArgGenerated}\n}\n groovyWrapper()`;
      // if index passed in, add to path. EX: (abc.def += [0]) => abc.def[0]
      outputValues.push({
        path: dynamicPath,
        value: groovyStr,
      });
    }
  } else {
    let groovyStr = `static def groovyWrapper() {\n return ${mappingValue}\n}\n groovyWrapper()`;

    groovyStr = groovyStr.replaceAll('(_)', `(${getSampleDataAsGroovyArg(sampleData)})`);

    const dunderMatches = mappingValue.match(/__\.\w+(\.\w+)+/g);

    if (dunderMatches) {
      dunderMatches.forEach(match => {
        const lookupArray = match.slice(3).split('.'); // substring(3) to remove __. prefix
        sampleDataValue = lookupObjectFromArray(sampleData, lookupArray);
        groovyStr = groovyStr.replace(match, `"${sampleDataValue}"`);
      });
    }

    const matches = mappingValue.match(/_\.\w+/g);

    if (matches) {
      matches.forEach(match => {
        sampleDataValue = sampleData[match.slice(2)]; // substring(2) to remove _. prefix
        groovyStr = groovyStr.replace(match, `"${sampleDataValue}"`);
      });
    }

    outputValues.push({
      path,
      value: groovyStr,
    });
  }

  // only send records we were able to find values for in the sampleData
  return outputValues.filter(ov => !ov.value.includes('undefined'));
};

export const expand = (pathList, mappingValue, metadata, dynamicIndex) => {
  if (pathList.length === 0) return getPathValue(mappingValue, metadata, dynamicIndex);
  const pathPart = pathList.shift();
  const index = extractFromPath(pathPart, 'index', true);
  const dynamicKeyname = extractFromPath(index, 'bucketName', false);
  const dynamicPathKeyword = extractFromPath(index, 'breakOutBy', false);
  const inputDataKey = extractFromPath(index, 'breakOutArg', false);

  const isList = index != null;

  if (isList) {
    if (dynamicPathKeyword === null) {
      return expandListPath(pathList, mappingValue, metadata);
    }

    if (dynamicPathKeyword === 'segment') {
      return expandDynamicPath(pathList, mappingValue, metadata, inputDataKey, dynamicKeyname);
    }

    return [];
  }

  return expand(pathList, mappingValue, metadata, dynamicIndex);
};

export const expandDynamicPath = (pathList, mappingValue, metadata, inputDataKey) => {
  const { sampleData } = metadata;
  let output = [];

  const sampleDataList = sampleData[inputDataKey];
  if (!sampleDataList) return [];

  sampleDataList.forEach((d, i) => {
    const expandedPaths = expand(pathList, mappingValue, metadata, i);
    output = output.concat(expandedPaths);
  });
  return output;
};

export const expandListPath = (path, mappingValue, metadata) => {
  return expand(path, mappingValue, metadata);
};
